import { FC, useState, useEffect } from "react";
import {
  IconButton,
  Box,
  CloseButton,
  Flex,
  Icon,
  Text,
  Drawer,
  DrawerContent,
  useDisclosure,
} from "@chakra-ui/react";
import { FiMenu } from "react-icons/fi";
import type {
  MobileProps,
  NavItemProps,
  SidebarProps,
  LayoutWrapperProps,
} from "./types";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Profile } from "../Profile";
import { StartMenu } from "../StartMenu";
import { Clippy } from "../Clippy";
import { RetroMusicPlayer } from "../RetroMusicPlayer";

export * from "./types";

export const LayoutWrapper: FC<LayoutWrapperProps> = (props) => {
  const { resourcesLinks } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isStartMenuOpen, setIsStartMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isMusicPlayerVisible, setIsMusicPlayerVisible] = useState(false);

  // Update clock every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      minH="100vh"
      bg="#008080"
      backgroundImage="radial-gradient(white, rgba(255,255,255,.1) 2px, transparent 3px)"
      backgroundSize="100px 100px"
      pb="40px" // Space for taskbar
    >
      <SidebarContent
        onClose={() => onClose}
        display={{ base: "none", md: "block" }}
        resourcesLinks={resourcesLinks}
      />
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <SidebarContent onClose={onClose} resourcesLinks={resourcesLinks} />
        </DrawerContent>
      </Drawer>
      <MobileNav display={{ base: "flex", md: "none" }} onOpen={onOpen} />
      <Box ml={{ base: 0, md: 60 }} p="4" minH="calc(100vh - 72px)">
        <Outlet />
      </Box>

      {/* Windows 95 Taskbar */}
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        h="40px"
        bg="linear-gradient(180deg, #C0C0C0, #A0A0A0)"
        borderTop="2px outset #FFFFFF"
        zIndex={1000}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
      >
        {/* Start Button */}
        <Box
          as="button"
          bg={isStartMenuOpen ? "#A0A0A0" : "#C0C0C0"}
          border={isStartMenuOpen ? "2px inset #808080" : "2px outset white"}
          px={3}
          py={1}
          fontFamily="'Comic Sans MS', cursive"
          fontWeight="bold"
          fontSize="sm"
          cursor="pointer"
          display="flex"
          alignItems="center"
          gap={2}
          _hover={{ bg: "#A0A0A0" }}
          onClick={() => setIsStartMenuOpen(!isStartMenuOpen)}
        >
          <Text fontSize="lg">🪟</Text>
          <Text color="black">Start</Text>
        </Box>

        {/* Quick Launch */}
        <Flex flex={1} mx={4} gap={1}>
          <Box
            as={Link}
            to="/me"
            bg="#C0C0C0"
            border="1px solid #808080"
            p={1}
            cursor="pointer"
            _hover={{ bg: "#A0A0A0" }}
            title="About Me"
          >
            👤
          </Box>
          <Box
            as={Link}
            to="/projects"
            bg="#C0C0C0"
            border="1px solid #808080"
            p={1}
            cursor="pointer"
            _hover={{ bg: "#A0A0A0" }}
            title="Projects"
          >
            💼
          </Box>
          <Box
            as={Link}
            to="/contact"
            bg="#C0C0C0"
            border="1px solid #808080"
            p={1}
            cursor="pointer"
            _hover={{ bg: "#A0A0A0" }}
            title="Contact"
          >
            📧
          </Box>
        </Flex>

        {/* System Tray */}
        <Flex align="center" gap={2}>
          {/* Volume Icon */}
          <Box
            bg="#C0C0C0"
            border="1px inset #808080"
            px={2}
            py={0.5}
            fontSize="xs"
          >
            🔊
          </Box>

          {/* Music Player Toggle - positioned right before the clock */}
          <Box
            as="button"
            bg={isMusicPlayerVisible ? "#A0A0A0" : "#C0C0C0"}
            border={
              isMusicPlayerVisible ? "1px inset #808080" : "1px solid #808080"
            }
            px={2}
            py={0.5}
            fontSize="xs"
            cursor="pointer"
            _hover={{ bg: "#A0A0A0" }}
            onClick={() => setIsMusicPlayerVisible(!isMusicPlayerVisible)}
            title="Winamp Music Player"
          >
            🎵
          </Box>

          {/* Clock */}
          <Box
            bg="#C0C0C0"
            border="2px inset #808080"
            px={3}
            py={1}
            fontFamily="'VT323', monospace"
            fontSize="sm"
            color="black"
          >
            {currentTime.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Box>
        </Flex>
      </Box>

      {/* Start Menu */}
      <StartMenu
        isOpen={isStartMenuOpen}
        onClose={() => setIsStartMenuOpen(false)}
      />

      {/* Clippy Assistant */}
      <Clippy />

      {/* Winamp Music Player - positioned at bottom right, above taskbar */}
      <RetroMusicPlayer
        isVisible={isMusicPlayerVisible}
        onToggle={() => setIsMusicPlayerVisible(false)}
      />
    </Box>
  );
};

const SidebarContent: FC<SidebarProps> = (props) => {
  const { onClose, resourcesLinks, ...rest } = props;
  const companyName = localStorage.getItem("companyName");

  return (
    <Box
      bg="linear-gradient(180deg, #C0C0C0, #A0A0A0)"
      border="3px outset #FFFFFF"
      borderRight="3px outset #808080"
      w={{ base: "full", md: 60 }}
      pos="fixed"
      h="calc(100% - 40px)"
      {...rest}
    >
      <Flex
        h="20"
        alignItems="center"
        bg="linear-gradient(90deg, #000080, #1084d0)"
        px={3}
        justifyContent="space-between"
        borderBottom="2px solid #808080"
      >
        <Text
          fontSize="lg"
          fontFamily="'Comic Sans MS', cursive"
          fontWeight="bold"
          color="white"
          textShadow="1px 1px 0px #000080"
        >
          📁 {companyName ?? "Yosri_Bedoui.exe"}
        </Text>
        <CloseButton
          display={{ base: "flex", md: "none" }}
          onClick={onClose}
          bg="#C0C0C0"
          border="2px outset white"
          borderRadius="0"
          _hover={{ bg: "#FF0000", color: "white" }}
        />
      </Flex>

      <Box p={2}>
        {resourcesLinks.map((link) => (
          <NavItem key={link.name} icon={link.icon} link={link.link}>
            {link.name}
          </NavItem>
        ))}
      </Box>

      <Profile />

      <Box textAlign="center" px={2}>
        <Text
          fontSize="xs"
          color="#000080"
          fontFamily="'Comic Sans MS', cursive"
          fontWeight="bold"
          sx={{
            animation: "color-cycle 3s infinite",
            "@keyframes color-cycle": {
              "0%": { color: "#FF0000" },
              "25%": { color: "#FF6600" },
              "50%": { color: "#FFFF00" },
              "75%": { color: "#00FF00" },
              "100%": { color: "#FF0000" },
            },
          }}
        >
          ⌨️ Writing code since 2018 ⌨️
        </Text>
      </Box>
    </Box>
  );
};

const NavItem: FC<NavItemProps> = (props) => {
  const { icon, children, link, ...rest } = props;
  const { pathname } = useLocation();
  const isActive = pathname === link;

  return (
    <Box
      as={Link}
      style={{ textDecoration: "none" }}
      _focus={{ boxShadow: "none" }}
      to={link}
    >
      <Flex
        align="center"
        p="3"
        mx="2"
        my="1"
        role="group"
        cursor="pointer"
        bg={isActive ? "#000080" : "#C0C0C0"}
        color={isActive ? "#FFFFFF" : "#000000"}
        border={isActive ? "2px inset #000080" : "2px outset #FFFFFF"}
        fontFamily="'Comic Sans MS', cursive"
        fontWeight="bold"
        _hover={{
          bg: isActive ? "#000080" : "#A0A0A0",
          border: "2px inset #808080",
        }}
        {...rest}
      >
        {icon && (
          <Icon
            mr="3"
            fontSize="18"
            color={isActive ? "#FFFF00" : "#000080"}
            _groupHover={{
              color: isActive ? "#FFFF00" : "#FF0000",
            }}
            as={icon}
          />
        )}
        {children}
      </Flex>
    </Box>
  );
};

const MobileNav: FC<MobileProps> = (props) => {
  const { onOpen, ...rest } = props;
  const companyName = localStorage.getItem("companyName");

  return (
    <Flex
      ml={{ base: 0, md: 60 }}
      px={{ base: 4, md: 24 }}
      height="16"
      alignItems="center"
      bg="linear-gradient(180deg, #C0C0C0, #A0A0A0)"
      borderBottom="3px outset #FFFFFF"
      justifyContent="flex-start"
      {...rest}
    >
      <IconButton
        variant="unstyled"
        onClick={onOpen}
        aria-label="open menu"
        icon={<FiMenu />}
        bg="#C0C0C0"
        border="2px outset white"
        borderRadius="0"
        color="black"
        _hover={{ bg: "#A0A0A0", border: "2px inset #808080" }}
      />
      <Text
        fontSize="xl"
        ml="4"
        fontFamily="'Comic Sans MS', cursive"
        fontWeight="bold"
        color="#000080"
        textShadow="1px 1px 0px #FFFFFF"
      >
        📁 {companyName ?? "Yosri_Bedoui.exe"}
      </Text>
    </Flex>
  );
};
